import { UserRole } from '../statuses';

/** Role groupings used for coarse routing and guard checks. */
export const ADMIN_ROLES: readonly UserRole[] = [UserRole.SUPER_ADMIN, UserRole.ADMIN];
export const SALES_ROLES: readonly UserRole[] = [UserRole.SALES_MANAGER, UserRole.SALES_REP];
export const CUSTOMER_ROLES: readonly UserRole[] = [UserRole.CUSTOMER_USER];
export const DISTRIBUTOR_ROLES: readonly UserRole[] = [
  UserRole.DISTRIBUTOR_ADMIN,
  UserRole.DISTRIBUTOR_USER,
];
export const INTERNAL_ROLES: readonly UserRole[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.FINANCE,
  UserRole.WAREHOUSE,
  UserRole.SALES_MANAGER,
  UserRole.SALES_REP,
  UserRole.DRIVER,
];

export const isAdminRole = (role: UserRole): boolean => ADMIN_ROLES.includes(role);
export const isSalesRole = (role: UserRole): boolean => SALES_ROLES.includes(role);
export const isDistributorRole = (role: UserRole): boolean => DISTRIBUTOR_ROLES.includes(role);
export const isInternalRole = (role: UserRole): boolean => INTERNAL_ROLES.includes(role);

export const PermissionModule = {
  PRODUCTS: 'products',
  INVENTORY_EXACT: 'inventory_exact',
  CUSTOMERS: 'customers',
  ORDERS: 'orders',
  PAYMENTS: 'payments',
  CREDIT_TERMS: 'credit_terms',
  DOCUMENTS: 'documents',
  REPORTS: 'reports',
  SETTINGS: 'settings',
} as const;
export type PermissionModule = (typeof PermissionModule)[keyof typeof PermissionModule];

/**
 * Coarse access level. `SCOPED` means "allowed, but restricted to rows the caller owns
 * or is assigned" — the precise own/assigned/territory filter is enforced in the service
 * layer, never inferred from this matrix alone.
 */
export const AccessLevel = {
  NONE: 'none',
  READ: 'read',
  SCOPED: 'scoped',
  FULL: 'full',
} as const;
export type AccessLevel = (typeof AccessLevel)[keyof typeof AccessLevel];

/** Coarse module access matrix derived from docs/05_ROLES_PERMISSIONS.md. */
export const ACCESS_MATRIX: Record<PermissionModule, Partial<Record<UserRole, AccessLevel>>> = {
  products: {
    admin: 'full',
    super_admin: 'full',
    finance: 'read',
    warehouse: 'read',
    sales_rep: 'read',
    sales_manager: 'read',
    customer_user: 'scoped',
    distributor_admin: 'scoped',
    distributor_user: 'scoped',
  },
  inventory_exact: {
    admin: 'full',
    super_admin: 'full',
    finance: 'read',
    warehouse: 'full',
    // Sales and distributors see only the availability label (products module), never exact quantities.
  },
  customers: {
    admin: 'full',
    super_admin: 'full',
    finance: 'read',
    warehouse: 'read',
    sales_rep: 'scoped',
    sales_manager: 'scoped',
    customer_user: 'scoped',
    distributor_admin: 'scoped',
    distributor_user: 'scoped',
  },
  orders: {
    admin: 'full',
    super_admin: 'full',
    finance: 'read',
    warehouse: 'scoped',
    sales_rep: 'scoped',
    sales_manager: 'scoped',
    customer_user: 'scoped',
    distributor_admin: 'scoped',
    distributor_user: 'scoped',
  },
  payments: {
    admin: 'full',
    super_admin: 'full',
    finance: 'full',
    sales_rep: 'scoped',
    customer_user: 'scoped',
    distributor_admin: 'scoped',
    distributor_user: 'scoped',
  },
  credit_terms: {
    admin: 'full',
    super_admin: 'full',
    finance: 'full',
    sales_rep: 'read',
    customer_user: 'read',
    distributor_admin: 'read',
  },
  documents: {
    admin: 'full',
    super_admin: 'full',
    finance: 'scoped',
    sales_rep: 'scoped',
    customer_user: 'scoped',
    distributor_admin: 'scoped',
    distributor_user: 'scoped',
  },
  reports: {
    admin: 'full',
    super_admin: 'full',
    finance: 'read',
    warehouse: 'scoped',
    sales_rep: 'scoped',
    sales_manager: 'scoped',
    distributor_admin: 'scoped',
  },
  settings: {
    admin: 'full',
    super_admin: 'full',
    finance: 'scoped',
    distributor_admin: 'scoped',
  },
};

/** Coarse access level for a role on a module. Always defaults to NONE. */
export function accessLevel(role: UserRole, module: PermissionModule): AccessLevel {
  return ACCESS_MATRIX[module][role] ?? AccessLevel.NONE;
}

/** True if the role has any access (read/scoped/full) to the module. */
export function canAccess(role: UserRole, module: PermissionModule): boolean {
  return accessLevel(role, module) !== AccessLevel.NONE;
}
